/**
 * Shared per-user boolean-preference store for the Agent SDK "door" pages
 * (Quality Co-Pilot, Tech Support Co-Pilot, …) — pure + unit-testable.
 *
 * A sibling of {@link ./conversationHistory.ts}'s `makeActiveConvStore`: same
 * per-user, per-door key model (`{prefix}{userId}`, blank/missing user → a shared
 * `anon` bucket) so a browser shared by two staff never crosses one person's
 * toggle onto another's session. Reads distinguish "stored false" from
 * "never set" (→ `null`) so a caller can apply its OWN default when nothing is
 * saved. localStorage access is wrapped so a privacy mode / quota error degrades
 * to a no-op (the preference just doesn't persist) rather than throwing.
 */

export interface BoolPrefStore {
  /** Per-user localStorage key for this preference. */
  key(userId: string | null | undefined): string;
  /**
   * Read the stored value, or `null` when nothing has been saved (so the caller
   * can fall back to its own default). Never throws.
   */
  read(userId: string | null | undefined): boolean | null;
  /** Read the stored value, or `fallback` when nothing is saved. Never throws. */
  readOr(userId: string | null | undefined, fallback: boolean): boolean;
  /** Persist the value for this user. Never throws. */
  write(userId: string | null | undefined, value: boolean): void;
}

/**
 * Build a per-user boolean-preference store. `prefix` is the localStorage key
 * prefix, INCLUDING the trailing underscore — e.g. `'cm_qcp_autoplay_'`.
 */
export function makeBoolPrefStore(prefix: string): BoolPrefStore {
  const keyFor = (userId: string | null | undefined): string =>
    `${prefix}${userId || 'anon'}`;
  const read = (userId: string | null | undefined): boolean | null => {
    try {
      const raw = window.localStorage.getItem(keyFor(userId));
      if (raw === null) return null;
      // Persisted as '1' / '0'; accept legacy 'true' / 'false' too.
      return raw === '1' || raw === 'true';
    } catch {
      return null;
    }
  };
  return {
    key: keyFor,
    read,
    readOr(userId, fallback) {
      const stored = read(userId);
      return stored === null ? fallback : stored;
    },
    write(userId, value) {
      try {
        window.localStorage.setItem(keyFor(userId), value ? '1' : '0');
      } catch {
        /* storage unavailable (private mode / quota) — preference just no-ops */
      }
    },
  };
}
